import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HeartHandshake, Banknote, Smartphone, Gift, Copy, Check } from "lucide-react";
import QRCODE from "../assets/QRCode.jpg";

const Donation = () => {
  const [copiedAcc, setCopiedAcc] = useState(false);
  const [copiedIfsc, setCopiedIfsc] = useState(false);

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === "acc") {
      setCopiedAcc(true);
      setTimeout(() => setCopiedAcc(false), 2000);
    } else {
      setCopiedIfsc(true);
      setTimeout(() => setCopiedIfsc(false), 2000);
    }
  };

  return (
    <section className="relative bg-gradient-to-b from-[#fffaf0] to-[#fff5e6] py-24 text-stone-800 overflow-hidden border-t border-amber-200">
      {/* Soft warm glow effect */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(251,191,36,0.04),transparent_70%)] pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        viewport={{ once: true }}
        className="max-w-6xl mx-auto px-6 flex flex-col items-center text-center"
      >
        {/* Title */}
        <span className="text-amber-800 font-semibold text-xs tracking-[0.25em] uppercase mb-3 block">
          Support Our Mission
        </span>
        <h2 className="text-3xl md:text-5xl font-serif font-bold mb-6 tracking-wide text-amber-950">
          Help with a Contribution of Your Choice
        </h2>
        <p className="text-stone-600 max-w-xl mb-16 font-light text-sm md:text-base leading-relaxed">
          Your kind donation directly supports our educational, nutritional, and spiritual initiatives. Every contribution makes a difference. 🙏
        </p>

        {/* Donation Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch w-full">
          {/* Bank Info */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white border border-amber-100/70 p-8 rounded-3xl shadow-sm text-left flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Banknote size={24} className="text-amber-700" />
                <h3 className="text-xl font-serif font-bold text-amber-950">
                  Bank Transfer (NEFT / RTGS)
                </h3>
              </div>

              <div className="space-y-4 text-sm md:text-base font-light text-stone-700">
                <div className="border-b border-amber-100/55 pb-2">
                  <p className="text-stone-400 text-xs uppercase tracking-wider">Bank Name</p>
                  <p className="font-semibold text-stone-900 mt-0.5">Punjab National Bank</p>
                </div>
                <div className="border-b border-amber-100/55 pb-2">
                  <p className="text-stone-400 text-xs uppercase tracking-wider">Account Name</p>
                  <p className="font-semibold text-stone-900 mt-0.5">Radha Krishna Gram Vikas Kendra</p>
                </div>
                <div className="border-b border-amber-100/55 pb-2 flex items-center justify-between">
                  <div>
                    <p className="text-stone-400 text-xs uppercase tracking-wider">Account Number</p>
                    <p className="font-mono font-semibold text-stone-900 mt-0.5">8561002100004221</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard("8561002100004221", "acc")}
                    className="p-2 bg-stone-50 border border-stone-200/50 hover:bg-amber-50 rounded-xl transition text-stone-500 hover:text-amber-700"
                    title="Copy Account Number"
                  >
                    {copiedAcc ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
                  </button>
                </div>
                <div className="border-b border-amber-100/55 pb-2 flex items-center justify-between">
                  <div>
                    <p className="text-stone-400 text-xs uppercase tracking-wider">IFSC Code</p>
                    <p className="font-mono font-semibold text-stone-900 mt-0.5">PUNB0856100</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard("PUNB0856100", "ifsc")}
                    className="p-2 bg-stone-50 border border-stone-200/50 hover:bg-amber-50 rounded-xl transition text-stone-500 hover:text-amber-700"
                    title="Copy IFSC Code"
                  >
                    {copiedIfsc ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* QR Section */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white border border-amber-100/70 p-8 rounded-3xl shadow-sm text-center flex flex-col items-center justify-center"
          >
            <div className="flex items-center gap-2 mb-4">
              <Smartphone size={22} className="text-amber-700" />
              <h3 className="text-xl font-serif font-bold text-amber-950">
                Donate via QR Code / UPI
              </h3>
            </div>

            <div className="relative p-3 bg-stone-50 border border-stone-100 rounded-2xl shadow-inner mb-4">
              <img
                src={QRCODE}
                alt="UPI QR CODE"
                className="h-48 w-48 object-contain"
              />
            </div>

            <p className="font-semibold text-stone-600 text-sm mb-5">
              UPI ID: <span className="text-amber-900 font-mono">7985980413m@pnb</span>
            </p>

            <Link
              to="https://tinyurl.com/22c9sfu6"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-bold text-sm tracking-wider uppercase px-6 py-3 rounded-xl transition-all duration-300 shadow-md"
            >
              <Gift size={16} /> Pay Now
            </Link>
          </motion.div>
        </div>

        {/* Bottom Icon */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          viewport={{ once: true }}
          className="flex justify-center mt-14"
        >
          <HeartHandshake size={36} className="text-amber-700/30" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Donation;
